import { useState, useEffect } from 'react';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import PageLayout from '../components/PageLayout';
import ArticleEditorForm, { ARTICLE_TITLE_ID } from '../components/ArticleEditorForm';
import { EXTERNAL_URL } from '../components/ArticleLinkField';
import useArticleDraft from '../components/useArticleDraft';
import usePlugins from '../plugins/usePlugins';
import { pluginArticle } from '../plugins/pluginBlocks';
import { pluginText } from '../plugins/pluginFrame';
import { createArticle, getAuthoringDepartments } from '../api';
import { stageDraftText, takeImportDraft } from '../import/importHandoff';
import { missingTitleMessage, titleErrorMessage } from '../utils/articleTitle';
import useAuth from '../auth/useAuth';
import PageHeader from '../components/ui/PageHeader';
import PageBody from '../components/ui/PageBody';
import PageMain from '../components/ui/PageMain';

/**
 * Creating an article and creating a news item are the same screen: the only
 * difference is the `is_news` flag and which list the result appears in.
 *
 * Nothing is chosen on arrival: the author picks the kind in the "Type" box
 * — only one whose manage right they hold, as the server refuses the request
 * without it anyway — and nothing is created until they do.
 */
/**
 * `linkMode`: a link item - title, summary and an address instead of a text;
 * opening it sends the reader to that address (components/ExternalLinkNotice).
 * /create-article/plugin/:pluginId: a block of a plugin, written in the
 * plugin's editor (plugins/PluginEditor).
 */
function CreateArticle({ linkMode = false }) {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { user } = useAuth();

  const location = useLocation();
  const { pluginId } = useParams();
  const plugins = usePlugins();
  const plugin = pluginId
    ? (plugins ?? []).find((p) => p.id === pluginId && p.block?.editor && p.ui?.createArticle) ?? null
    : null;

  // null until the author picks article (false) or news (true).
  const [isNews, setIsNews] = useState(null);

  const canWriteArticles =
    user?.rightsAnywhere?.includes('articles.manage') ?? false;
  const canWriteNews =
    user?.rightsAnywhere?.includes('news.manage') ?? false;

  const draft = useArticleDraft({
    title: location.state?.title || '',
    text: location.state?.text || '',
    tags: location.state?.tags || [],
  });

  const [saving, setSaving] = useState(false);

  // Departments this author may write in. One is applied by the server without
  // asking; several require an explicit choice, so the picker only appears then.
  const [departments, setDepartments] = useState([]);
  const [departmentId, setDepartmentId] = useState(null);

  // A course imported on /create-article/rise: its pictures are staged like
  // ones the author inserted, so they upload only when the article is saved.
  useEffect(() => {
    const imported = takeImportDraft(location.state?.importId);

    if (!imported) {
      return;
    }

    draft.load({
      ...draft.values,
      title: imported.title,
      summary: imported.summary,
      text: stageDraftText(imported, draft.stage),
    });

    if (canWriteArticles) {
      setIsNews(false);
    }
    // Runs once, for the draft the page was opened with.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    let cancelled = false;

    // The kind changed: the department list is per kind, so an old choice
    // may no longer be on offer.
    setDepartmentId(null);

    if (isNews === null) {
      setDepartments([]);
      return undefined;
    }

    getAuthoringDepartments(isNews)
      .then((list) => {
        if (!cancelled) setDepartments(list ?? []);
      })
      .catch((loadError) => {
        // Not fatal: an author outside every department simply gets no picker,
        // and the server still decides what the article is bound to.
        console.error('Failed to load authoring departments:', loadError);
      });

    return () => { cancelled = true; };
  }, [isNews]);

  async function handleCreate() {
    const { title, text, tags, icon, summary, externalUrl } = draft.values;

    const missing = missingTitleMessage(title, t);
    draft.setTitleError(missing ?? '');
    if (missing) {
      document.getElementById(ARTICLE_TITLE_ID)?.focus();
      return;
    }

    if (isNews === null) {
      draft.setError(t('createArticle.pickKind'));
      return;
    }

    if (departments.length > 1 && departmentId === null) {
      draft.setError(t('createArticle.pickDepartment'));
      return;
    }

    if (linkMode && !EXTERNAL_URL.test(externalUrl)) {
      draft.setError(t('articleLink.invalid'));
      return;
    }

    if (pluginId && !(plugin && pluginArticle(text, [plugin]))) {
      draft.setError(t('plugins.empty'));
      return;
    }


    try {
      setSaving(true);
      draft.setError('');

      const finalText = linkMode
        ? ''
        : await draft.uploadPending(text, isNews ? 'news' : 'article');

      // Uploaded now: the draft keeps the stored URLs if the save below fails.
      if (!linkMode && finalText !== text) draft.set('text', finalText);

      const article = await createArticle({
        title,
        text: finalText,
        tags,
        icon,
        summary,
        ...(linkMode ? { external_url: externalUrl } : {}),
        ...(isNews ? { is_news: true } : {}),
        ...(departmentId === null
          ? {}
          : { department_id: departmentId }),
      });

      navigate(
        isNews
          ? `/news/${article.article_id}`
          : `/article/${article.article_id}`,
      );
    } catch (err) {
      console.error('Artikel konnte nicht erstellt werden:', err);

      const titleProblem = titleErrorMessage(err, title, t);
      if (titleProblem) {
        draft.setTitleError(titleProblem);
        document.getElementById(ARTICLE_TITLE_ID)?.focus();
      } else {
        draft.setError(err?.message || t('createArticle.createError'));
      }
    } finally {
      setSaving(false);
    }
  }

  function handleCancel() {
    draft.discardPending();
    navigate(-1);
  }

  return (
    <PageLayout
      showSearch
      showAiSearch
      sidebarClosed={Boolean(pluginId)}
    >
      <PageMain className={`article-page${pluginId ? ' page-main--wide' : ''}`}>

        <PageHeader
          title={plugin
            ? pluginText(plugin.ui.createArticle.title, i18n.language)
            : t(linkMode ? 'articleLink.createTitle' : 'createArticle.title')}
          condensed
        />

        <PageBody className="page-body--split">
          {pluginId && plugins && !plugin ? (
            <p className="plugin-block plugin-block--off" role="alert">{t('plugins.notAvailable')}</p>
          ) : pluginId && !plugin ? null : (
          <ArticleEditorForm
            draft={draft}
            linkMode={linkMode}
            plugin={plugin}
            pluginScope={departmentId}
            showLinkHint
            canReimport={canWriteArticles}
            // An imported PDF or course opens in preview, to check the
            // converted content before editing it.
            preview={location.state?.importId ? 'preview' : 'edit'}
            saving={saving}
            onSubmit={handleCreate}
            onCancel={handleCancel}
            labels={{
              title: t('createArticle.titleLabel'),
              linkHint: t('articleLink.hint'),
              cancel: t('createArticle.cancelButton'),
              submit: t(isNews ? 'newsPage.publishButtonLabel' : 'createArticle.createButton'),
              saving: t(isNews ? 'newsPage.publishingLabel' : 'createArticle.creatingButton'),
            }}
            uploadTarget={(isNews ?? !canWriteArticles) ? 'news' : 'article'}
            departments={departments}
            departmentId={departmentId}
            onDepartmentChange={setDepartmentId}
            sidebar={{
              isNews,
              onKindChange: setIsNews,
              canWriteArticles,
              canWriteNews,
            }}
          />
          )}
        </PageBody>

      </PageMain>
    </PageLayout>
  );
}

export default CreateArticle;
